import React, { useMemo } from 'react';
import { Text, Group } from 'react-konva';

import FlashbackLayout from 'common/components/RewindModal/Konvas/FlashbackLayout';
import KonvaImage from 'common/components/RewindModal/Konvas/KonvaImage';
import ShareableStage from 'common/components/RewindModal/Konvas/ShareableStage';
import {
  LAYER_WIDTH,
  BLOCK_MARGIN,
  FLASHBACK_COVER_LOGO_WIDTH,
  FLASHBACK_COVER_LOGO_HEIGHT,
  FLASHBACK_COVER_LOGO_TOP_MARGIN,
  FLASHBACK_COVER_LOGO_OPACITY,
  ALLOCINE_LOGO_WIDTH,
  ALLOCINE_LOGO_HEIGHT,
  ALLOCINE_LOGO_BOTTOM_MARGIN,
  FLASHBACK_BACKGROUND_COLOR,
  FLASHBACK_FONT_FAMILY,
  FLASHBACK_USER_RANKING_TITLE_FONT_SIZE,
  FLASHBACK_USER_RANKING_TITLE_FONT_WEIGHT,
  FLASHBACK_USER_RANKING_TITLE_LINE_HEIGHT,
  FLASHBACK_USER_RANKING_TITLE_COLOR,
  FLASHBACK_USER_RANKING_SUBTITLE_FONT_SIZE,
  FLASHBACK_USER_RANKING_SUBTITLE_FONT_WEIGHT,
  FLASHBACK_USER_RANKING_SUBTITLE_LINE_HEIGHT,
  FLASHBACK_USER_RANKING_SUBTITLE_COLOR,
  FLASHBACK_USER_RANKING_LORD_IMAGE_WIDTH,
  FLASHBACK_USER_RANKING_LORD_IMAGE_HEIGHT,
  FLASHBACK_USER_RANKING_PERCENT_IMAGE_WIDTH,
  FLASHBACK_USER_RANKING_PERCENT_IMAGE_HEIGHT,
  FLASHBACK_USER_RANKING_PERCENT_IMAGE_CORNER_RADIUS,
  FLASHBACK_USER_RANKING_LOGO_TO_LORD_IMAGE_SPACING,
  FLASHBACK_USER_RANKING_LORD_IMAGE_TO_TITLE_SPACING,
  FLASHBACK_USER_RANKING_TITLE_TO_SUBTITLE_SPACING,
  FLASHBACK_USER_RANKING_SUBTITLE_TO_PERCENT_IMAGE_SPACING
} from 'common/components/RewindModal/Konvas/constants';
import {
  SEIGNEUR_DES_NOTES,
  IMG_12_PERCENT
} from 'common/configuration/constants';

type ScreenUserRankingProps = {
  data: {
    title?: string;
    subtitle?: string;
  };
};

const ShareableScreenUserRanking = React.forwardRef<
  any,
  ScreenUserRankingProps
>(({ data }, ref) => {
  const title = data?.title ?? 'Le seigneur des notes';
  const subtitle =
    data?.subtitle ?? "Comme Sauron, on vous appelle l'oeil qui voit tout.";

  const layout = useMemo(() => {
    const lordImageHeight = FLASHBACK_USER_RANKING_LORD_IMAGE_HEIGHT;
    const percentImageHeight = FLASHBACK_USER_RANKING_PERCENT_IMAGE_HEIGHT;

    const titleHeight =
      FLASHBACK_USER_RANKING_TITLE_FONT_SIZE *
      FLASHBACK_USER_RANKING_TITLE_LINE_HEIGHT;
    const subtitleHeight =
      FLASHBACK_USER_RANKING_SUBTITLE_FONT_SIZE *
      FLASHBACK_USER_RANKING_SUBTITLE_LINE_HEIGHT;

    const y_lordImage =
      FLASHBACK_COVER_LOGO_TOP_MARGIN +
      FLASHBACK_COVER_LOGO_HEIGHT +
      FLASHBACK_USER_RANKING_LOGO_TO_LORD_IMAGE_SPACING;
    const y_title =
      y_lordImage +
      lordImageHeight +
      FLASHBACK_USER_RANKING_LORD_IMAGE_TO_TITLE_SPACING;
    const y_subtitle =
      y_title + titleHeight + FLASHBACK_USER_RANKING_TITLE_TO_SUBTITLE_SPACING;
    const y_percentImage =
      y_subtitle +
      subtitleHeight +
      FLASHBACK_USER_RANKING_SUBTITLE_TO_PERCENT_IMAGE_SPACING;

    const contentHeight = y_percentImage + percentImageHeight;

    return {
      y_lordImage,
      y_title,
      y_subtitle,
      y_percentImage,
      contentHeight
    };
  }, []);

  const centerX = LAYER_WIDTH / 2;

  return (
    <ShareableStage ref={ref}>
      <FlashbackLayout
        backgroundColor={FLASHBACK_BACKGROUND_COLOR}
        backgroundOpacity={1}
        coverLogoWidth={FLASHBACK_COVER_LOGO_WIDTH}
        coverLogoHeight={FLASHBACK_COVER_LOGO_HEIGHT}
        coverLogoTopMargin={FLASHBACK_COVER_LOGO_TOP_MARGIN}
        coverLogoOpacity={FLASHBACK_COVER_LOGO_OPACITY}
        allocineLogoWidth={ALLOCINE_LOGO_WIDTH}
        allocineLogoHeight={ALLOCINE_LOGO_HEIGHT}
        allocineLogoBottomMargin={ALLOCINE_LOGO_BOTTOM_MARGIN}
      >
        <Group>
          <Group
            x={centerX}
            y={layout.y_lordImage}
            offsetX={FLASHBACK_USER_RANKING_LORD_IMAGE_WIDTH / 2}
            offsetY={0}
          >
            <KonvaImage
              path={SEIGNEUR_DES_NOTES}
              isLocal={true}
              width={FLASHBACK_USER_RANKING_LORD_IMAGE_WIDTH}
              height={FLASHBACK_USER_RANKING_LORD_IMAGE_HEIGHT}
            />
          </Group>

          <Text
            text={title}
            fontSize={FLASHBACK_USER_RANKING_TITLE_FONT_SIZE}
            fontFamily={FLASHBACK_FONT_FAMILY}
            fontStyle={FLASHBACK_USER_RANKING_TITLE_FONT_WEIGHT}
            lineHeight={FLASHBACK_USER_RANKING_TITLE_LINE_HEIGHT}
            fill={FLASHBACK_USER_RANKING_TITLE_COLOR}
            width={LAYER_WIDTH - BLOCK_MARGIN * 4}
            x={BLOCK_MARGIN * 2}
            align="center"
            y={layout.y_title}
          />

          <Text
            text={subtitle}
            fontSize={FLASHBACK_USER_RANKING_SUBTITLE_FONT_SIZE}
            fontFamily={FLASHBACK_FONT_FAMILY}
            fontStyle={FLASHBACK_USER_RANKING_SUBTITLE_FONT_WEIGHT}
            lineHeight={FLASHBACK_USER_RANKING_SUBTITLE_LINE_HEIGHT}
            fill={FLASHBACK_USER_RANKING_SUBTITLE_COLOR}
            width={LAYER_WIDTH - BLOCK_MARGIN * 4}
            x={BLOCK_MARGIN * 2}
            align="center"
            y={layout.y_subtitle}
          />

          <Group
            x={centerX}
            y={layout.y_percentImage}
            offsetX={FLASHBACK_USER_RANKING_PERCENT_IMAGE_WIDTH / 2}
            offsetY={0}
          >
            <KonvaImage
              path={IMG_12_PERCENT}
              isLocal={true}
              width={FLASHBACK_USER_RANKING_PERCENT_IMAGE_WIDTH}
              height={FLASHBACK_USER_RANKING_PERCENT_IMAGE_HEIGHT}
              cornerRadius={FLASHBACK_USER_RANKING_PERCENT_IMAGE_CORNER_RADIUS}
            />
          </Group>
        </Group>
      </FlashbackLayout>
    </ShareableStage>
  );
});

ShareableScreenUserRanking.displayName = 'ShareableScreenUserRanking';
export default ShareableScreenUserRanking;
